import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser';
import { environment } from '../../../../../environments/environment';

@Injectable()
export class MsalInterceptorInterceptor implements HttpInterceptor {

  constructor(private msalService: MsalService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isApiRequest(req.url)) {
      return next.handle(req);
    }

    const account = this.msalService.instance.getActiveAccount()
      ?? this.msalService.instance.getAllAccounts()[0];

    if (!account) {
      return next.handle(req);
    }

    return this.acquireToken(account).pipe(
      switchMap(response => next.handle(this.withToken(req, response.accessToken)).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status !== 401) {
            return throwError(() => error);
          }

          return this.acquireToken(account, true).pipe(
            switchMap(refreshed => next.handle(this.withToken(req, refreshed.accessToken)))
          );
        })
      ))
    );
  }

  private acquireToken(account: AccountInfo, forceRefresh = false): Observable<AuthenticationResult> {
    return this.msalService.acquireTokenSilent({
      scopes: environment.apiScopes,
      account,
      forceRefresh
    }).pipe(
      tap(response => sessionStorage.setItem('accessToken', response.accessToken)),
      catchError(error => {
        if (error instanceof InteractionRequiredAuthError) {
          return this.msalService.acquireTokenRedirect({
            scopes: environment.apiScopes,
            account
          }).pipe(switchMap(() => throwError(() => error)));
        }

        return throwError(() => error);
      })
    );
  }

  private withToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      setHeaders: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...(req.body instanceof FormData ? {} : { 'Content-Type': 'application/json; charset=utf-8' })
      }
    });
  }

  private isApiRequest(url: string): boolean {
    const requestUrl = new URL(url, window.location.origin);

    return [environment.apiUrl, environment.ioTestApiUrl, environment.apiUrlCodexAIDiagramGenius]
      .some(resource => {
        const apiUrl = new URL(resource);
        const apiPath = apiUrl.pathname.replace(/\/$/, '');

        return requestUrl.origin === apiUrl.origin
          && (requestUrl.pathname === apiPath || requestUrl.pathname.startsWith(`${apiPath}/`));
      });
  }
}
